import { REQUIRED_MESSAGE, requiredString } from '@wyz/utils';
import { z } from 'zod';
import { isNil } from 'lodash-es';
import { TYRE_IDENTIFIER_FIELD } from '@wyz/types';

const priceFrameTypeSchema = z.enum(['BF_OFFICIAL', 'BF_EXPANDED'], {
  error: (issue) =>
    issue.input === undefined ? REQUIRED_MESSAGE : REQUIRED_MESSAGE,
});

export const WIDTH_REQUIRED_MESSAGE =
  'app.setting_template.form.search_parameters.width_required';
export const WIDTH_MIN_NOT_LOWER_THAN_MAX_MESSAGE =
  'app.setting_template.form.search_parameters.width_min_not_lower_than_max';

export const vehicleTypeGroupSchema = z.object({
  publicKey: z.string(),
  externalId: z.number(),
  i18nKey: z.string(),
  name: z.string(),
  vehicleTypes: z.array(
    z.object({
      externalId: z.number(),
      name: z.string(),
      publicKey: z.string(),
      i18nKey: z.string(),
    }),
  ),
});

export const settingTemplateFormSchema = z.object({
  name: requiredString,
  criteria: z
    .array(
      z.object({
        criterion: z.object({ id: z.string() }),
        values: z.array(requiredString).min(1, { message: REQUIRED_MESSAGE }),
      }),
    )
    .min(1, { message: REQUIRED_MESSAGE }),
  alternativeOfferSetting: z
    .object({
      j0: z.boolean().nullable(),
      j0Today: z.boolean().nullish(),
      j0NextDay: z.boolean().nullish(),
      bestDelay: z.boolean().nullable(),
      bestPrice: z.boolean().nullable(),
      j0MaxQuantity: z.number().nullish(),
    })
    .superRefine(({ j0, j0MaxQuantity, j0Today, j0NextDay }, ctx) => {
      if (j0 && j0Today && isNil(j0MaxQuantity)) {
        ctx.addIssue({
          code: 'custom',
          message: REQUIRED_MESSAGE,
          path: ['j0MaxQuantity'],
          fatal: true,
        });
      }
      if (j0 && !j0Today && !j0NextDay) {
        ctx.addIssue({
          code: 'custom',
          message: REQUIRED_MESSAGE,
          path: ['j0Today'],
          fatal: true,
        });
      }
    }),
  generalSetting: z
    .object({
      backOrder: z.boolean().nullable(),
      referenceGroupPublicKey: z.string().nullable(),
      alternativeReferenceEnabled: z.boolean().nullable(),
      alternativeSupplierPriceFrameType: requiredString,
      wholesalerSupplierPriceFrameType: priceFrameTypeSchema,
      tyreIdentifierFields: z.array(z.enum(TYRE_IDENTIFIER_FIELD)).nullish(),
      displayBestPriceTag: z.boolean().nullable(),
      displayBestDelayTag: z.boolean().nullable(),
      manualSearch: z.boolean(),
      defaultSeasons: z.array(z.string()),
      defaultHomologations: z.array(z.string()),
      defaultWidthMin: z.number().nullable(),
      defaultWidthMax: z.number().nullable(),
      destockingEnabled: z.boolean(),
    })
    .superRefine(({ defaultWidthMin, defaultWidthMax }, ctx) => {
      (['defaultWidthMin', 'defaultWidthMax'] as const).forEach((path) => {
        if (isNil({ defaultWidthMin, defaultWidthMax }[path])) {
          ctx.addIssue({
            code: 'custom',
            message: WIDTH_REQUIRED_MESSAGE,
            path: [path],
            fatal: true,
          });
        }
      });

      if (
        !isNil(defaultWidthMin) &&
        !isNil(defaultWidthMax) &&
        defaultWidthMin >= defaultWidthMax
      ) {
        // a single issue, on the Min field only, so the message is rendered once
        ctx.addIssue({
          code: 'custom',
          message: WIDTH_MIN_NOT_LOWER_THAN_MAX_MESSAGE,
          path: ['defaultWidthMin'],
          fatal: true,
        });
      }
    }),
  vehicleTypeGroups: z.array(vehicleTypeGroupSchema),
});

export const settingTemplatePayload = settingTemplateFormSchema
  .omit({ criteria: true })
  .extend({
    criteria: z.array(
      z.object({
        criterion: z.object({ id: z.string() }),
        values: z.array(
          z.object({
            id: z.string(),
            label: z.string(),
          }),
        ),
      }),
    ),
  });
export const settingTemplateSchema = settingTemplateFormSchema
  .omit({ criteria: true })
  .extend({
    id: z.string(),
    criteria: z.array(
      z.object({
        criterion: z.object({ id: z.string(), label: z.string() }),
        values: z.array(
          z.object({
            id: z.string(),
            label: z.string(),
          }),
        ),
      }),
    ),
  });
